/* =========================================================================
   KPI Sports — Club Shop attribution reconciliation.
   Loaded site-wide from layout/theme.liquid (defer).

   Why: club-shop attribution (Player / _Role / _Team / _Position) is only
   attached by the add-to-cart flows on the club-shop collection page. Items
   added anywhere else — the Qikify Tepo widget on product pages, direct
   product-page adds — reach the cart without it. If a recent quiz selection
   is stored (written by club-shop-v2-6.js under 'cs-selection'), this
   stamps the missing properties onto those lines via /cart/change.js before
   checkout. Only products that appear in the club collection's pool are
   covered — items from other collections (e.g. supporters merch) are left
   alone by design.

   Safety rules:
   - Only touches lines whose product id is in the stored club product list.
   - Never overwrites an existing Player, and never changes a line whose
     _Role/_Team/_Position differ from the stored selection (so one child's
     items are never relabelled with a sibling's details).
   - Existing properties (e.g. Tepo's Name / _optionSetId) are preserved —
     attribution is merged in, never replacing anything.
   ========================================================================= */
(function () {
  'use strict';

  var KEY = 'cs-selection';
  var TTL_MS = 24 * 60 * 60 * 1000; // must match STORE_TTL_MS in club-shop-v2-6.js
  var MAX_PASSES = 15;              // hard stop — one pass per changed line
  var running = false;

  function getSelection() {
    try {
      var obj = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (!obj || !obj.ts || (Date.now() - obj.ts) >= TTL_MS) return null;
      var sel = obj.v;
      if (!sel) return null;
      if (!(sel.team || sel.role || sel.position)) return null;
      if (!sel.products || !sel.products.length) return null;
      return sel;
    } catch (e) { return null; }
  }

  function desiredProps(sel) {
    var p = {};
    if (sel.playerName) p['Player'] = sel.playerName;
    if (sel.firstName) p['_First Name'] = sel.firstName;
    if (sel.lastName) p['_Last Name'] = sel.lastName;
    if (sel.role) p['_Role'] = sel.role;
    if (sel.team) p['_Team'] = sel.team;
    if (sel.position) p['_Position'] = sel.position;
    return p;
  }

  function clone(obj) {
    var out = {};
    Object.keys(obj || {}).forEach(function (k) { out[k] = obj[k]; });
    return out;
  }

  function sameProps(a, b) {
    var ka = Object.keys(a), kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    for (var i = 0; i < kb.length; i++) {
      if (a[kb[i]] !== b[kb[i]]) return false;
    }
    return true;
  }

  // The full property set a line SHOULD have, or null to leave it alone.
  function mergedPropsFor(item, sel) {
    if (sel.products.indexOf(item.product_id) === -1) return null;
    var props = item.properties || {};
    var want = desiredProps(sel);
    var hasAttribution = props['_Team'] || props['_Role'] || props['_Position'];

    if (!hasAttribution) {
      // Unattributed club line (Tepo / product-page / merch add). A Player
      // value with no _Team shouldn't exist, but if it does, it's someone
      // else's — leave it.
      if (props['Player']) return null;
      var merged = clone(props);
      Object.keys(want).forEach(function (k) { if (!merged[k]) merged[k] = want[k]; });
      return sameProps(props, merged) ? null : merged;
    }

    // Attributed but missing name fields (nameless pre-fix lines, or lines
    // stamped before the first/last split existed): fill them only when
    // everything else matches this selection — and never when the line
    // already carries a DIFFERENT player — so a sibling's items keep their
    // own attribution.
    if ((props['_Team'] || '') === (sel.team || '') &&
        (props['_Role'] || '') === (sel.role || '') &&
        (props['_Position'] || '') === (sel.position || '')) {
      if (props['Player'] && props['Player'] !== want['Player']) return null;
      var m2 = clone(props);
      var filled = false;
      ['Player', '_First Name', '_Last Name'].forEach(function (k) {
        if (want[k] && !m2[k]) { m2[k] = want[k]; filled = true; }
      });
      return filled ? m2 : null;
    }
    return null;
  }

  // Changing properties changes the line key (and can merge lines), so fix
  // one line per pass and re-read the cart until nothing is left to stamp.
  function pass(sel, depth) {
    if (depth >= MAX_PASSES) { running = false; return; }
    fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('cart read failed');
        return r.json();
      })
      .then(function (cart) {
        var items = cart.items || [];
        var target = null;
        var props = null;
        for (var i = 0; i < items.length; i++) {
          props = mergedPropsFor(items[i], sel);
          if (props) { target = items[i]; break; }
        }
        if (!target) { running = false; return; }
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: target.key, quantity: target.quantity, properties: props }),
          credentials: 'same-origin'
        }).then(function (r) {
          if (!r.ok) throw new Error('cart change failed');
          pass(sel, depth + 1);
        });
      })
      .catch(function () { running = false; });
  }

  function reconcile() {
    if (running) return;
    var sel = getSelection();
    if (!sel) return;
    running = true;
    pass(sel, 0);
  }

  // Run once per page load…
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }

  // …and again right after anything on this page (Tepo widget, theme quick
  // add, other apps) adds to the cart, so product-page adds are stamped
  // without needing another navigation. Classic form posts navigate away —
  // the next page's load-time run covers those.
  (function hookCartAdds() {
    if (window.fetch) {
      var origFetch = window.fetch;
      window.fetch = function () {
        var url = arguments[0] && (arguments[0].url || arguments[0]);
        var p = origFetch.apply(this, arguments);
        if (typeof url === 'string' && url.indexOf('/cart/add') !== -1) {
          p.then(function () { setTimeout(reconcile, 500); }, function () {});
        }
        return p;
      };
    }
    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (typeof url === 'string' && url.indexOf('/cart/add') !== -1) {
        this.addEventListener('load', function () { setTimeout(reconcile, 500); });
      }
      return origOpen.apply(this, arguments);
    };
  })();
})();
